import { get } from "lodash";
import { mapGetters, mapState } from "vuex";

import conditionalUtil from "@/lib/conditionalUtil";
import {
  validateExists,
  validateMaxLengthFactory,
  validateMinLengthFactory,
} from "@/lib/validate";

export default {
  created() {
    if (
      this.mode === "create" &&
      this.valueDefault &&
      (this.value === null || this.value === undefined)
    ) {
      this.value = this.valueDefault;
    }

    this.configureValidatorList();
  },
  computed: {
    id() {
      const { entity, idAttributeName } = this;

      return entity[idAttributeName];
    },
    completeRequired() {
      const { entity, conditionalList, value } = this;

      if (!(value === undefined || value === null)) {
        return false;
      }

      return !!conditionalUtil.resolveAction({
        actionId: "require",
        conditionalList,
        entity,
      });
    },
    disabled() {
      const {
        attributeName,
        conditionalList,
        disabledOverride,
        entity,
        entityIsLocked,
        entityPermissionMap,
        mode,
      } = this;

      const buildIsClosed = this.organizationStatusIsGreaterThanOrEqualTo(
        "buildClosed"
      );

      if (buildIsClosed) {
        return buildIsClosed;
      }

      if (entityIsLocked) {
        return entityIsLocked;
      }

      // Get the granular attribute-level permission and if it does not exist, use the entity-level permission.
      const { update } = entityPermissionMap;
      const allowUpdate = get(
        entityPermissionMap,
        `attribute[${attributeName}].update`,
        update
      );
      
      if (mode !== "create" && allowUpdate === false) {
        return true;
      }

      if (typeof disabledOverride === "boolean") {
        return disabledOverride;
      }

      return !!conditionalUtil.resolveAction({
        actionId: "disable",
        conditionalList,
        entity,
      });
    },
    shouldRender() {
      const {
        attributeName,
        conditionalList,
        entity,
        entityPermissionMap,
      } = this;

      if (this.isBatchEdit) {
        return true;
      }

      const { view } = entityPermissionMap;

      // Some attributes should only be visible to OCHIN staff, i.e. department.abbreviation.
      // Get the granular attribute-level permission and if it does not exist, use the entity-level permission.
      const showAttribute = get(
        entityPermissionMap,
        `attribute[${attributeName}].view`,
        view
      );

      return !!conditionalUtil.resolveAction({
        actionId: "show",
        conditionalList,
        defaultValue: showAttribute,
        entity,
      });
    },
    entityIsLocked() {
      return !!this.entity.locked;
    },
    entityPermissionMap() {
      const { entityName } = this;
      return get(this, `session.user.permission.${entityName}`);
    },
    styleMap() {
      return {
        filled: this.componentStyle === "filled",
        outlined: this.componentStyle === "outlined",
        solo: this.componentStyle === "solo",
      };
    },
    value: {
      get() {
        const { attributeName, entity } = this;

        return entity[attributeName];
      },
      set(value) {
        const { attributeName, entity } = this;

        if (
          value === undefined ||
          (typeof value === "string" && value === "") ||
          (Array.isArray(value) && value.length === 0)
        ) {
          value = null;
        }

        // Add reactive property
        this.$set(entity, attributeName, value);

        this.$emit("change", entity);
      },
    },
    ...mapGetters({
      getUserAttributeByName: "userAttribute/getById",
      organizationStatusIsGreaterThanOrEqualTo:
        "organizationStatus/organizationStatusIsGreaterThanOrEqualTo",
    }),
    ...mapState({
      session: state => state.session,
    }),
  },
  data() {
    return {
      validatorList: [],
    };
  },
  methods: {
    configureValidatorList() {
      const { counter, minLength, required, ruleList } = this;

      this.validatorList = this.validatorList.concat(ruleList);

      if (required) {
        this.validatorList.push(validateExists);
      }

      if (minLength) {
        this.validatorList.push(validateMinLengthFactory(minLength));
      }

      if (counter) {
        this.validatorList.push(validateMaxLengthFactory(counter));
      }
    },
  },
  model: {
    prop: "entity",
    event: "change",
  },
  props: {
    attribute: {},
    attributeName: {
      type: String,
    },
    disabledOverride: {
      type: Boolean,
      default: null,
    },
    comboboxOptionList: {
      type: Array,
    },
    componentStyle: {
      default: "outlined",
      type: String,
    },
    conditionalList: {
      type: Array,
    },
    counter: {
      type: Number,
    },
    entity: {
      // Default entity to an empty object
      default: () => ({}),
      type: Object,
    },
    entityName: {
      required: true,
      type: String,
    },
    helpText: {
      type: String,
    },
    idAttributeName: {
      type: String,
    },
    isBatchEdit: {
      default: false,
      type: Boolean,
    },
    label: {
      type: String,
    },
    minLength: {
      type: Number,
    },
    multiple: {
      type: Boolean,
    },
    placeholder: {},
    required: {
      type: Boolean,
    },
    ruleList: {
      type: Array,
    },
    showHeader: {
      default: true,
      type: Boolean,
    },
    valueDefault: {},
    mode: {},
  },
  watch: {
    entity: {
      deep: true,
      handler: function (entity) {
        if (!entity) {
          return;
        }

        const { conditionalList } = this;
        const actionId = "setValue";

        const action = conditionalUtil.resolveAction({
          actionId,
          conditionalList,
          entity,
        });

        if (!action) {
          return;
        }

        const { valueDefault } = action;

        this.$set(entity, this.attributeName, valueDefault);
      },
    },
  },
};
